<script setup>
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import PageGuidance from '@/components/guidance/PageGuidance.vue'
import PageHeader from '@/components/shared/PageHeader.vue'
import StatusChip from '@/components/shared/StatusChip.vue'
import { railsApi } from '@/utils/railsApi'

definePage({
  meta: {
    allowOnboarding: true,
  },
})

const { t } = useI18n()

const loading = ref(false)
const saving = ref(false)
const clearing = ref(false)
const testing = ref(false)
const error = ref('')
const success = ref('')
const testFeedback = ref(null)
const connection = ref(null)
const apiKeyInput = ref('')

const saveDialog = ref(false)
const clearDialog = ref(false)

const formatWhen = value => {
  if (!value)
    return '—'

  return new Date(value).toLocaleString('nl-NL')
}

const configuredLabel = computed(() => {
  if (!connection.value)
    return ''
  if (!connection.value.configured)
    return t('mollieConnection.status.notConfigured')
  if (connection.value.mode === 'live')
    return t('mollieConnection.status.configuredLive')
  if (connection.value.mode === 'test')
    return t('mollieConnection.status.configuredTest')

  return t('mollieConnection.status.configuredUnknownMode')
})

const canClear = computed(() => !!connection.value?.configured)
const canSave = computed(() => apiKeyInput.value.trim().length > 0)

const load = async () => {
  loading.value = true
  error.value = ''
  try {
    const data = await railsApi('/api/v1/mollie_connection')

    connection.value = data.mollie_connection
  }
  catch (err) {
    error.value = err.payload?.error || err.message || t('mollieConnection.loadError')
  }
  finally {
    loading.value = false
  }
}

onMounted(load)

const openSaveDialog = () => {
  if (!canSave.value)
    return
  error.value = ''
  success.value = ''
  testFeedback.value = null
  saveDialog.value = true
}

const confirmSave = async () => {
  saving.value = true
  error.value = ''
  success.value = ''
  testFeedback.value = null
  try {
    const data = await railsApi('/api/v1/mollie_connection', {
      method: 'PATCH',
      body: {
        mollie_connection: { api_key: apiKeyInput.value.trim() },
      },
    })

    connection.value = data.mollie_connection
    apiKeyInput.value = ''
    saveDialog.value = false
    success.value = t('mollieConnection.saveSuccess')
  }
  catch (err) {
    error.value = err.payload?.errors?.[0] || err.payload?.error || err.message || t('mollieConnection.saveError')
  }
  finally {
    saving.value = false
  }
}

const confirmClear = async () => {
  clearing.value = true
  error.value = ''
  success.value = ''
  testFeedback.value = null
  try {
    const data = await railsApi('/api/v1/mollie_connection', {
      method: 'DELETE',
    })

    connection.value = data.mollie_connection
    clearDialog.value = false
    success.value = t('mollieConnection.clearSuccess')
  }
  catch (err) {
    error.value = err.payload?.errors?.[0] || err.payload?.error || err.message || t('mollieConnection.clearError')
  }
  finally {
    clearing.value = false
  }
}

const testConnection = async () => {
  testing.value = true
  error.value = ''
  success.value = ''
  testFeedback.value = null
  try {
    const data = await railsApi('/api/v1/mollie_connection/test', {
      method: 'POST',
    })

    connection.value = data.mollie_connection
    testFeedback.value = data.test || {
      success: false,
      message: t('mollieConnection.testError'),
    }
  }
  catch (err) {
    testFeedback.value = {
      success: false,
      message: err.payload?.errors?.[0] || err.payload?.error || err.message || t('mollieConnection.testError'),
    }
  }
  finally {
    testing.value = false
  }
}
</script>

<template>
  <div>
    <PageHeader
      :title="$t('mollieConnection.title')"
      :subtitle="$t('mollieConnection.subtitle')"
    >
      <template #actions>
        <PageGuidance />
      </template>
    </PageHeader>

    <VAlert
      v-if="error"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ error }}
    </VAlert>
    <VAlert
      v-if="success"
      type="success"
      variant="tonal"
      class="mb-4"
      role="status"
    >
      {{ success }}
    </VAlert>

    <div
      v-if="loading"
      role="status"
    >
      {{ $t('common.loading') }}
    </div>

    <template v-else-if="connection">
      <VCard
        flat
        border
        class="mb-4"
        data-tour="mollie-status"
      >
        <VCardTitle>{{ $t('mollieConnection.statusTitle') }}</VCardTitle>
        <VCardText>
          <div class="d-flex flex-wrap align-center ga-2 mb-3">
            <span class="text-body-1">{{ configuredLabel }}</span>
            <StatusChip
              v-if="connection.mode"
              domain="mollie_mode"
              :state="connection.mode"
            />
          </div>

          <div
            class="mb-0"
            role="status"
            aria-live="polite"
          >
            <p class="mb-1">
              <strong>{{ $t('mollieConnection.lastTest') }}:</strong>
              <template v-if="connection.last_test_outcome">
                {{ formatWhen(connection.last_tested_at) }}
              </template>
              <template v-else>
                {{ $t('mollieConnection.noTestYet') }}
              </template>
            </p>
            <div
              v-if="connection.last_test_outcome"
              class="d-flex flex-wrap align-center ga-2"
            >
              <StatusChip
                domain="mollie_test_outcome"
                :state="connection.last_test_outcome"
              />
              <span class="text-body-2">{{ connection.last_test_message }}</span>
            </div>
          </div>
        </VCardText>
      </VCard>

      <VCard
        flat
        border
        class="mb-4"
        data-tour="mollie-key"
      >
        <VCardTitle>{{ $t('mollieConnection.keyTitle') }}</VCardTitle>
        <VCardText>
          <VAlert
            type="info"
            variant="tonal"
            class="mb-4"
            role="status"
          >
            {{ $t('mollieConnection.writeOnlyHelp') }}
          </VAlert>

          <VTextField
            v-model="apiKeyInput"
            type="password"
            autocomplete="new-password"
            :label="$t('mollieConnection.apiKeyLabel')"
            :hint="$t('mollieConnection.apiKeyHint')"
            persistent-hint
            class="mb-4"
            hide-details="auto"
          />

          <div class="d-flex flex-wrap ga-2">
            <VBtn
              color="primary"
              :disabled="!canSave || saving || clearing"
              @click="openSaveDialog"
            >
              {{ connection.configured
                ? $t('mollieConnection.rotateKey')
                : $t('mollieConnection.saveKey') }}
            </VBtn>
            <VBtn
              color="error"
              variant="tonal"
              :disabled="!canClear || saving || clearing"
              @click="clearDialog = true"
            >
              {{ $t('mollieConnection.clearKey') }}
            </VBtn>
          </div>
        </VCardText>
      </VCard>

      <VCard
        flat
        border
        data-tour="mollie-test"
      >
        <VCardTitle>{{ $t('mollieConnection.testTitle') }}</VCardTitle>
        <VCardText>
          <p class="text-body-2 text-medium-emphasis mb-4">
            {{ $t('mollieConnection.testHelp') }}
          </p>
          <VBtn
            color="primary"
            variant="tonal"
            :loading="testing"
            :disabled="saving || clearing"
            @click="testConnection"
          >
            {{ $t('mollieConnection.testConnection') }}
          </VBtn>

          <VAlert
            v-if="testFeedback"
            class="mt-4"
            :type="testFeedback.success ? 'success' : 'error'"
            variant="tonal"
            :role="testFeedback.success ? 'status' : 'alert'"
          >
            {{ testFeedback.message }}
          </VAlert>
        </VCardText>
      </VCard>
    </template>

    <ConfirmDialog
      v-model="saveDialog"
      :title="connection?.configured
        ? $t('mollieConnection.rotateConfirmTitle')
        : $t('mollieConnection.saveConfirmTitle')"
      :body="connection?.configured
        ? $t('mollieConnection.rotateConfirmBody')
        : $t('mollieConnection.saveConfirmBody')"
      :confirm-label="connection?.configured
        ? $t('mollieConnection.rotateKey')
        : $t('mollieConnection.saveKey')"
      :loading="saving"
      @confirm="confirmSave"
    />

    <ConfirmDialog
      v-model="clearDialog"
      :title="$t('mollieConnection.clearConfirmTitle')"
      :body="$t('mollieConnection.clearConfirmBody')"
      :confirm-label="$t('mollieConnection.clearKey')"
      :acknowledgment-label="$t('mollieConnection.clearAcknowledge')"
      require-acknowledgment
      color="error"
      :loading="clearing"
      @confirm="confirmClear"
    />
  </div>
</template>
